import type { ContractArticleSuggestionsQueryParams } from './useQueryContractArticleSuggestions';
import type { ContractDuplicateCheckParams } from './useQueryContractDuplicateCheck';
import type { ContractItemsQueryParams } from './useQueryContractItems';
import type {
  ContractPartiesQueryParams,
  ContractPartyLookupParams,
} from './useQueryContractParties';
import type { ContractsQueryParams } from './useQueryContracts';

import type { UUID } from '~/types/common';

import { unique } from '@vestigas/utils';

/**
 * Query key factory for contract-related queries.
 */
export const queryKeysContract = {
  base: () => 'contracts' as const,
  get: (contractId: UUID) =>
    [queryKeysContract.base(), 'detail', { contractId }] as const,
  getActions: (contractId: UUID) =>
    [queryKeysContract.base(), 'actions', { contractId }] as const,
  getAll: (queryParams?: Partial<ContractsQueryParams>) => {
    const normalized = queryParams
      ? {
          ...queryParams,
          accountingReferenceIds: queryParams.accountingReferenceIds
            ? unique(queryParams.accountingReferenceIds)
                .filter(Boolean)
                .toSorted()
            : undefined,
          siteIds: queryParams.siteIds
            ? unique(queryParams.siteIds).filter(Boolean).toSorted()
            : undefined,
        }
      : queryParams;

    return [queryKeysContract.base(), 'list', normalized].filter(Boolean);
  },
  getArticleSuggestions: (
    queryParams?: Partial<ContractArticleSuggestionsQueryParams>,
  ) =>
    [queryKeysContract.base(), 'article-suggestions', queryParams].filter(
      Boolean,
    ),
  getBulk: (ids: UUID[]) =>
    [
      queryKeysContract.base(),
      'bulk',
      {
        ids: unique([ids].flat().filter(Boolean).toSorted()),
      },
    ] as const,
  getDuplicateCheck: (
    params: Partial<ContractDuplicateCheckParams> | undefined,
  ) => [queryKeysContract.base(), 'duplicate-check', params ?? {}] as const,
  getFilterParties: (queryParams: Partial<ContractPartiesQueryParams>) =>
    [queryKeysContract.base(), 'filter-parties', 'list', queryParams] as const,
  getFilterParty: (queryParams: ContractPartyLookupParams) =>
    [
      queryKeysContract.base(),
      'filter-parties',
      'detail',
      queryParams,
    ] as const,
  getHistory: (contractId: UUID) =>
    [queryKeysContract.base(), 'history', { contractId }] as const,
  getItems: (
    contractId: UUID,
    queryParams: Partial<ContractItemsQueryParams> | undefined,
  ) =>
    [queryKeysContract.base(), 'items', { contractId }, queryParams].filter(
      Boolean,
    ),
  getPermissions: (contractId: UUID) =>
    [queryKeysContract.base(), 'permissions', { contractId }] as const,
  getPublicView: (contractId: UUID) =>
    [queryKeysContract.base(), 'public-view', { contractId }] as const,
} as const;
